import { useEffect, useState } from 'react'

import { Check, Mail, Phone } from 'lucide-react'

import { resolveContactChannels } from '../../../lib/contact-channels'

import type { LucideIcon } from 'lucide-react'

const BASE =
  'inline-flex items-center border border-[#bbf7d0] bg-gis-green-tint font-semibold text-gis-green hover:bg-[#dcfce7] transition focus-visible:ring-2 focus-visible:ring-blue-500/60'
const SIZE = {
  md: 'gap-[7px] px-3 py-2 text-[11.5px] rounded-[10px]',
  sm: 'gap-[5px] px-[9px] py-[5px] text-[11px] rounded-[9px]',
} as const
const ICON = { md: 'h-3.5 w-3.5', sm: 'h-3 w-3' } as const

/** A contact chip that copies its value to the clipboard on click (per request — replaces the old
 *  tel:/mailto: links), with a brief ✓ "Copied" confirmation. Shows the value in FULL (long emails wrap
 *  within the chip rather than truncating). */
function CopyPill({
  value,
  label,
  icon: Icon,
  size,
}: {
  value: string
  label: string
  icon: LucideIcon
  size: 'sm' | 'md'
}) {
  const [copied, setCopied] = useState(false)
  // Auto-clear the confirmation (with cleanup so it never fires after unmount).
  useEffect(() => {
    if (!copied) {
      return
    }
    const t = setTimeout(() => setCopied(false), 1200)
    return () => clearTimeout(t)
  }, [copied])

  const onClick = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (navigator.clipboard) {
      void navigator.clipboard.writeText(value)
      setCopied(true)
    }
  }

  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={`Copy ${label} ${value} to clipboard`}
      title={copied ? 'Copied!' : 'Click to copy'}
      className={`${BASE} ${SIZE[size]} max-w-full text-left`}
    >
      {copied ? (
        <Check className={`${ICON[size]} flex-none stroke-[2.4]`} />
      ) : (
        <Icon className={`${ICON[size]} flex-none stroke-2`} />
      )}
      <span className="break-all tabular-nums">{value}</span>
    </button>
  )
}

/** Adaptive contact: a copy-to-clipboard chip for a phone, one for an email, both if both, and `null`
 *  when neither — so callers never render a dead affordance. The chips wrap to their own line when the
 *  row is narrow (so a full email is never squeezed). `size='sm'` is the dense variant; `'md'` (default)
 *  suits hero rows. */
export function ContactPill({
  phone,
  email,
  size = 'md',
}: {
  phone?: string | null
  email?: string | null
  size?: 'sm' | 'md'
}) {
  const c = resolveContactChannels({ phone, email })
  if (!c.hasAny) {
    return null
  }
  return (
    <div className={`flex min-w-0 flex-wrap items-center ${size === 'sm' ? 'gap-1.5' : 'gap-2'}`}>
      {c.phone && <CopyPill value={c.phone} label="phone" icon={Phone} size={size} />}
      {c.email && <CopyPill value={c.email} label="email" icon={Mail} size={size} />}
    </div>
  )
}
